import { Component, OnInit, signal, effect, Output, EventEmitter, input, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { Store } from '@ngrx/store';

import { GridModule } from '@progress/kendo-angular-grid';
import { KENDO_BUTTONS } from '@progress/kendo-angular-buttons';
import { KENDO_COMBOBOX } from "@progress/kendo-angular-dropdowns";
import { KENDO_INPUTS } from "@progress/kendo-angular-inputs";
import { KENDO_LABELS } from "@progress/kendo-angular-label";
import { IconsModule } from '@progress/kendo-angular-icons';
import { KENDO_DATEINPUTS } from '@progress/kendo-angular-dateinputs';
import { KENDO_DROPDOWNS } from "@progress/kendo-angular-dropdowns";

import { DateForPmis } from '@pmis3/utils';
import { AppState } from '@pmis3/data-access';
import { ControlsModule, Pmis3UiPageTemplateComponent } from '@pmis3/ui-controls';
import { selectVesselVisits, VesselVisitHelper, ComponentLoaded
    , IVesselVisit, IVesselTransitStatus, IVesselActivityPurposeDropdown, ILadenStatusDropdown, IServiceApplicationTypes
    , LoadAgentOrganization
    , selectAgentOrganization, selectTransitStatuses
    , UpdateOriginalSelectedVesselVisit, ExitEditNOAMode
    , UpdateOriginalSelectedVesselVisitForTestData
    , LoadInputPPAData
    , IVesselMovement, VesselActivityType
 } from '@pmis3/vessel-activity-core';
import { ISeaport } from '@pmis3/ui-features';
import { ILocationType, IOrganization, IOrganizationType, IVessel, IVesselOrientation, ICargoType, ICargoUnitType, ICargoOperation, ILocationForNOA } from '@pmis3/master-data-core'

import { VesselVisitDetailComponent } from "./vessel-visit-detail/vessel-visit-detail.component";
import { VesselVisitGridItemComponent } from './vessel-visit-grid-item/vessel-visit-grid-item.component';
import { PPAObjectMappingHelper } from 'libs/vessel-activity/vessel-activity-core/src/lib/vessel-visit/ppaObjectMapping.helper';


@Component({
    selector: 'pmis3-vessel-visit',
    standalone: true,
    imports: [
        CommonModule,
        GridModule,
        ControlsModule,
        Pmis3UiPageTemplateComponent,
        VesselVisitGridItemComponent,
        VesselVisitDetailComponent,
        KENDO_BUTTONS,
        KENDO_COMBOBOX,
        KENDO_INPUTS,
        KENDO_LABELS,
        FormsModule,
        IconsModule,
        KENDO_DATEINPUTS,
        KENDO_DROPDOWNS
    ],
    templateUrl: './pmis3-vessel-visit.component.html',
    styleUrl: './pmis3-vessel-visit.component.scss',
})
export class Pmis3VesselVisitComponent implements OnInit {
    @Output() submitNOA = new EventEmitter<any>();
    @Output() saveNOA = new EventEmitter<any>();
    @Output() getVesselVisitById = new EventEmitter<number>();
    @Output() from = new EventEmitter<Date>();
    @Output() to = new EventEmitter<Date>();
    @Output() searchQuery = new EventEmitter<string>();
    @Output() statusFilter = new EventEmitter<number[]>();
    @Output() getVesselVisits = new EventEmitter();

    statuses = input<IVesselTransitStatus[]>([]);
    vessels = input<IVessel[]>([]);
    locations = input<ILocationForNOA[]>([]);
    locationTypes = input<ILocationType[]>([]);
    vesselOrientations = input<IVesselOrientation[]>([]);
    vesselVisitDTOs = input<any[]>([]);
    organizationTypes = input<IOrganizationType[]>([]);
    organizations = input<IOrganization[]>([]);
    vesselActivityPurposes = input<IVesselActivityPurposeDropdown[]>([]);
    ladenStatuses = input<ILadenStatusDropdown[]>([]);
    serviceApplicationTypes = input<IServiceApplicationTypes[]>([]);
    vesselVisit = input<any>(undefined);
    agentOrganizationId = input<number | undefined>(undefined);
    cargoTypes = input<ICargoType[]>([]);
    cargoUnitTypes = input<ICargoUnitType[]>([]);
    cargoOperations = input<ICargoOperation[]>([]);
    seaports = input<ISeaport[]>([]);

    fromDate = signal<DateForPmis>(new DateForPmis())
    toDate = signal<DateForPmis>(new DateForPmis(new DateForPmis().getTime() + 14 * 24 * 60 * 60 * 1000))

    vesselVisits = this.store.selectSignal(selectVesselVisits)
    agentOrganization = this.store.selectSignal(selectAgentOrganization);
    statusDropdown = this.store.selectSignal(selectTransitStatuses);
    displayNOAList = signal(true);

    editExistingVesselVisit = signal(false);

    selectedStatusFilters = signal<number[]>([]);
    onSelectedStatusChange(statuses: number[]) {
        this.statusFilter.emit(statuses);
    }

    query = signal<string | undefined>('')
    currentOrHistory = signal<"current" | "history">("current");

    constructor(private store: Store<AppState>) {
        this.store.dispatch(ComponentLoaded()); // for loading test data

        effect(() => {
            const vesselsDTO = this.vessels()
            const locations = this.locations()
            const vesselVisitDTOs = this.vesselVisitDTOs();
            const organizationTypes = this.organizationTypes();
            const locationTypes = this.locationTypes();
            const seaports = this.seaports();
            const organizations = this.organizations();
            const purposes = this.vesselActivityPurposes();
            const vesselOrientations = this.vesselOrientations();
            const ladenStatuses = this.ladenStatuses();
            const serviceApplicationTypes = this.serviceApplicationTypes();
            const cargoTypes = this.cargoTypes();
            const cargoUnitTypes = this.cargoUnitTypes();
            const transitStatuses = this.statuses();
            const cargoOperations = this.cargoOperations();

            const areAllArraysNonEmpty = [
                vesselsDTO,
                locations,
                organizationTypes,
                locationTypes,
                organizations,
                purposes,
                vesselOrientations,
                ladenStatuses,
                serviceApplicationTypes,
                cargoTypes,
                cargoUnitTypes,
                transitStatuses,
                // cargoOperations // uncomment when this info is passed from PPA
            ]?.every(arr => arr != undefined && arr.length > 0);

            if (areAllArraysNonEmpty) {
                const locationsForNOA = PPAObjectMappingHelper.filterLocationsForNOA(locations);
                const terminals = PPAObjectMappingHelper.filterTerminalsAndPilotStationDropDown(locationsForNOA);
                const berths = PPAObjectMappingHelper.mapBerthsForNOA(locationsForNOA);
                const vessels = vesselsDTO.map(dto => PPAObjectMappingHelper.mapVesselDTOToVessel(dto));
                const vesselVisits = vesselVisitDTOs.map(dto => PPAObjectMappingHelper.mapVesselVisitGridDTOToVesselVisitGrid(dto, locations));
                const mappedOrganizationTypes = organizationTypes.map(dto => PPAObjectMappingHelper.mapOrganizationTypeDTOToOrganizationType(dto));
                this.store.dispatch(LoadInputPPAData({
                    vessels: vessels,
                    locations: berths,
                    terminals: terminals,
                    locationTypes: this.locationTypes(),
                    vesselVisits: vesselVisits,
                    organizationTypes: mappedOrganizationTypes,
                    organizations: this.organizations(),
                    purposes: this.vesselActivityPurposes(),
                    vesselOrientations: this.vesselOrientations(),
                    ladenStatuses: this.ladenStatuses(),
                    serviceApplicationTypes: this.serviceApplicationTypes(),
                    cargoTypes: cargoTypes,
                    cargoUnitTypes: cargoUnitTypes,
                    cargoOperations: cargoOperations,
                    transitStatuses: transitStatuses
                }))
            }
        }, { allowSignalWrites: true })

        effect(() => {
            const vesselVisitDTO = this.vesselVisit();
            if (vesselVisitDTO) {
                const vesselVisit = PPAObjectMappingHelper.mapVesselVisitDTOToVesselVisit(
                  vesselVisitDTO, this.locations(), this.vessels());
                this.store.dispatch(UpdateOriginalSelectedVesselVisit({ vesselVisit }))
            }
        }, { allowSignalWrites: true })

        effect(() => {
            const agentOrganizationId = this.agentOrganizationId();
            const organizations = this.organizations();

            if (agentOrganizationId && organizations) {
                const agentOrganization = organizations?.find(org => org.id == agentOrganizationId.toString())
                if (agentOrganization) {
                    this.store.dispatch(LoadAgentOrganization({ organization: agentOrganization }))
                }
            }
        }, { allowSignalWrites: true })
    }

    ngOnInit(): void {
        this.from.emit(this.fromDate());
        this.to.emit(this.toDate());
    }

    onNewNOA() {
        const newVesselVisit = VesselVisitHelper.createNewVesselVisit(this.agentOrganization())
        this.store.dispatch(UpdateOriginalSelectedVesselVisit({ vesselVisit: newVesselVisit }))
        this.displayNOAList.update(display => !display);
        this.editExistingVesselVisit.set(false);
    }

    onBackClicked() {
        this.store.dispatch(ExitEditNOAMode());
        this.displayNOAList.update(display => !display);
        this.getVesselVisits.emit()
    }

    onQueryChange(query: string) {
        this.searchQuery.emit(query)
    }

    onSearch() {
        this.getVesselVisits.emit()
    }

    onVesselVisitSelected(vvId: number) {
        this.editExistingVesselVisit.set(true);
        this.displayNOAList.update(display => !display);
        this.getVesselVisitById.emit(Number(vvId));
        this.store.dispatch(UpdateOriginalSelectedVesselVisitForTestData({ vesselVisitId: vvId })) // for loading test data
    }

    onSubmitNOA(vesselVisit: IVesselVisit) {
        const mappedVV = PPAObjectMappingHelper.mapVesselVisitToVesselVisitDTO(vesselVisit);
        this.submitNOA.emit(mappedVV);
    }

    onSaveNOA(vesselVisit: IVesselVisit) {
        const mappedVV = PPAObjectMappingHelper.mapVesselVisitToVesselVisitDTO(vesselVisit);
        this.saveNOA.emit(mappedVV);
    }

    onFromDateChange(fromDate: Date) {
        if (fromDate < this.toDate()) {
            this.from.emit(fromDate)
        }
    }
    onToDateChange(toDate: Date) {
        if (toDate > this.fromDate()) {
            this.to.emit(toDate)
        }
    }
}